'use client';
import { useEffect, useMemo, useState } from 'react';

import { SidebarItem } from '@/types';
import { isAutomated, isLeague, isNonAutomated } from './useSportsSidebar';

interface SportCategoryDataProps {
  currentCategoryData: SidebarItem | null;
  activeId: string | number | null;
  effectiveSportSlug: string;
  gameId?: string;
  activeTab: 'games' | 'props';
  setActiveTab: (tab: 'games' | 'props') => void;
  resetSelections: () => void;
}

/**
 * Custom hook for resolving category metadata (series_id, tag_id, etc.)
 * and managing category state transitions.
 */
export const useSportCategoryData = ({
  currentCategoryData,
  activeId,
  effectiveSportSlug,
  gameId,
  activeTab,
  setActiveTab,
  resetSelections,
}: SportCategoryDataProps) => {
  const [lastActiveId, setLastActiveId] = useState(activeId);

  /** Checks if the current view should have game-related content. */
  const getSeriesId = () => {
    if (isLeague(currentCategoryData)) {
      return currentCategoryData.series ? Number(currentCategoryData.series) : null;
    }
    if (isAutomated(currentCategoryData) && currentCategoryData.leagues?.length > 0) {
      return currentCategoryData.leagues[0].series
        ? Number(currentCategoryData.leagues[0].series)
        : null;
    }
    return null;
  };
  const series_id = getSeriesId();

  const hasGames = effectiveSportSlug === 'live' || !!series_id;

  // Synchronize state during render when the category changes (React pattern for resetting state)
  if (activeId !== lastActiveId) {
    setLastActiveId(activeId);
    resetSelections();

    // Determine the ideal tab for the new category
    const nextTab = gameId ? 'props' : hasGames ? 'games' : 'props';
    if (nextTab !== activeTab) {
      setActiveTab(nextTab);
    }
  } else if (!hasGames && activeTab === 'games' && !gameId) {
    setActiveTab('props');
  }

  /** Determines the numeric Tag ID for the current category. */
  const getTagId = () => {
    if (!currentCategoryData) {
      return effectiveSportSlug === 'live' ? 100639 : null;
    }

    if (isLeague(currentCategoryData) && currentCategoryData.tags) {
      return Number(currentCategoryData.tags);
    }
    if (isNonAutomated(currentCategoryData) && currentCategoryData.leagues?.tag) {
      return Number(currentCategoryData.leagues.tag);
    }
    if (isAutomated(currentCategoryData)) {
      if (currentCategoryData.tags) return Number(currentCategoryData.tags);
      if (currentCategoryData.leagues?.length > 0 && currentCategoryData.leagues[0].tags) {
        return Number(currentCategoryData.leagues[0].tags);
      }
    }

    return null;
  };
  const tag_id = getTagId();

  const showComments = !!currentCategoryData && isAutomated(currentCategoryData);

  return {
    series_id,
    tag_id,
    hasGames,
    showComments,
  };
};
